import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Container,
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import JoditEditor from 'jodit-react';
import AdminLayout from '../Admin_Layout/AdminLayout';
import { useLocation, useNavigate } from 'react-router-dom';

const AddBlogPost = () => {
  const location = useLocation();
  const { update = false, formData = {} } = location.state || {};
  const editor = useRef(null);
  const navigate = useNavigate();

  const [blogData, setBlogData] = useState({
    title: '',
    author: '',
    imageURL: '',
    authorImage: '',
    shortDescription: '',
    longDescription: '',
    status: 'unpublished',  // Default status
    ...formData
  });

  useEffect(() => {
    if (update && formData) {
      setBlogData(formData);
    }
  }, [update, formData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBlogData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleStatusChange = (e) => {
    setBlogData((prev) => ({
      ...prev,
      status: e.target.value
    }));
  };

  const config = useMemo(() => ({
    readonly: false,
    placeholder: "Enter Long Description",
    events: {
      change: (newContent) => {
        setBlogData((prev) => ({ ...prev, longDescription: newContent }));
      },
      focus: () => {
        if (editor.current) {
          editor.current.focus();
        }
      }
    }
  }), []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('blog data:', JSON.stringify(blogData));
    const token = localStorage.getItem('token');
    const endpoint = update
      ? `${process.env.REACT_APP_API_URL}/api/blogs/${formData._id}`
      : `${process.env.REACT_APP_API_URL}/api/blogs`;
    const method = update ? 'PUT' : 'POST';

    try {
      const response = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` // ✅ Add Bearer Token here
        },
        body: JSON.stringify(blogData)
      });

      const result = await response.json();
      if (!response.ok) {
        console.log('Error details:', result);
        throw new Error(result.message || 'Failed to save blog post');
      }

      alert(update ? 'Blog post updated successfully!' : 'Blog post added successfully!');
      navigate('/admin-dashboard/manage-blogs/update-blogs');
      console.log('Saved blog post:', result);
    } catch (error) {
      console.error('Error saving blog post:', error);
      alert(`Error saving blog post: ${error.message}`);
    }
  };

  return (
    <AdminLayout>
      <Box sx={{ p: { xs: 2, sm: 3 }, pt: { xs: 8, sm: 9 } }}>
        <Container maxWidth="lg">
          <Paper
            elevation={0}
            sx={{
              p: 3,
              bgcolor: 'background.paper',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Typography variant="h4" gutterBottom>
              {update ? 'Update Blog Post' : 'Add New Blog Post'}
            </Typography>

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
              <Grid container spacing={3}>
                {/* Title */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Title"
                    name="title"
                    value={blogData.title}
                    onChange={handleInputChange}
                    placeholder="Enter Blog Title"
                    required
                  />
                </Grid>

                {/* Author */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Author"
                    name="author"
                    value={blogData.author}
                    onChange={handleInputChange}
                    placeholder="Enter Author Name"
                    required
                  />
                </Grid>

                {/* Status Dropdown */}
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={blogData.status}
                      onChange={handleStatusChange}
                      label='status'
                    >
                      <MenuItem value="published">Published</MenuItem>
                      <MenuItem value="unpublished">Unpublished</MenuItem>
                      <MenuItem value="archived">Archived</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Image URL */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Image URL"
                    name="imageURL"
                    value={blogData.imageURL}
                    onChange={handleInputChange}
                    placeholder="Enter Image URL"
                    required
                  />
                </Grid>

                {/* AuthorImage URL */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="AuthorImage "
                    name="authorImage"
                    value={blogData.auhorImage}
                    onChange={handleInputChange}
                    placeholder="Enter authorImage URL"
                    required
                  />
                </Grid>

                {/* Short Description */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Short Description"
                    name="shortDescription"
                    value={blogData.shortDescription}
                    onChange={handleInputChange}
                    multiline
                    rows={3}
                    required
                    inputProps={{ maxLength: 250 }}
                    helperText={`${blogData.shortDescription.length}/250 characters`}
                  />
                </Grid>

                {/* Long Description - Jodit Editor */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Long Description
                  </Typography>
                  <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <JoditEditor
                      ref={editor}
                      value={blogData.longDescription}
                      config={config}
                      tabIndex={1}
                    />
                  </Box>
                </Grid>

                {/* Submit Button */}
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    sx={{ mt: 2 }}
                  >
                    {update ? 'Update Blog Post' : 'Add Blog Post'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Container>
      </Box>
    </AdminLayout>
  );
};

export default AddBlogPost;
